import { useCallback, useEffect, useRef, useState } from "react";
import { X, Mic } from "lucide-react";
import { http } from "@/lib/api";
import { speech, spokenBody, closingLine, takeSentences, tts } from "@/lib/speech";
import { CeresMark } from "@/components/ceres/CeresMark";

const SPEECH_RMS = 0.03;
const SILENCE_MS = 800;
const NO_SPEECH_MS = 12000;
const BARGE_RMS = 0.05;
const BARGE_TICKS = 2;
const MAX_SPOKEN = 3; // sentences read aloud; the full answer stays on screen
const ackCache = {};
const ACKS = {
  en: ["Let me check that.", "One moment.", "Sure, looking that up.", "Good question. Checking now."],
  fr: ["Je vérifie.", "Un instant.", "Bien sûr, je regarde.", "Bonne question. Je vérifie."],
};
const MAX_MS = 25000;
const pickMime = () => ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const plain = (t) => t.split(/Commissions, management fees|Des commissions, des frais/)[0]
  .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\s*\[(\d+)\]/g, "").replace(/^\s*[-*•]\s+/gm, "").replace(/[*_#`>|]/g, "")
  .replace(/\s+([.,;:!?])/g, "$1").replace(/\s+/g, " ").trim();

// Opens mic + audio context inside the user's tap so mobile browsers allow capture and playback.
export const openVoiceSession = async () => {
  speech.unlock();
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  ctx.resume?.();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  ctx.createMediaStreamSource(stream).connect(analyser);
  return { ctx, stream, analyser };
};

const useListener = (session, lang, onHeard, onSilentTimeout, onLevel) => {
  const stopRef = useRef(null);
  const listen = useCallback((preheard = false) => {
    stopRef.current?.(false); // cancel any previous recorder so only one listens at a time
    const { stream, analyser } = session;
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const chunks = [];
    const buf = new Uint8Array(analyser.fftSize);
    const t0 = Date.now();
    let heard = preheard;
    let lastLoud = preheard ? Date.now() : 0;
    let manual = false;
    let cancelled = false;
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const tick = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      onLevel(Math.min(1, rms * 8));
      const now = Date.now();
      if (rms > SPEECH_RMS) { heard = true; lastLoud = now; }
      if ((heard && now - lastLoud > SILENCE_MS) || now - t0 > MAX_MS || (!heard && now - t0 > NO_SPEECH_MS)) end();
    }, 100);
    const end = () => {
      clearInterval(tick);
      onLevel(0);
      if (rec.state === "recording") rec.stop();
    };
    rec.onstop = () => {
      if (cancelled) return;
      if (!heard && !manual) return onSilentTimeout();
      const type = rec.mimeType || mime || "audio/webm";
      onHeard(new Blob(chunks, { type }), type);
    };
    rec.start(250);
    stopRef.current = (send) => { manual = send; if (!send) { heard = false; cancelled = true; } end(); };
  }, [session, onHeard, onSilentTimeout, onLevel]);
  return { listen, stop: (send = true) => stopRef.current?.(send) };
};

export const VoiceMode = ({ s, lang, session, messages, send, cancel, onExit }) => {
  const [phase, setPhase] = useState("listening");
  const [level, setLevel] = useState(0);
  const [heardText, setHeardText] = useState("");
  const [note, setNote] = useState("");
  const active = useRef(true);
  const waiting = useRef(false);
  const opened = useRef(null);
  const spoken = useRef(0);
  const said = useRef(0);
  const turn = useRef(0);
  const monitor = useRef(null);

  const stopMonitor = () => { clearInterval(monitor.current); monitor.current = null; };
  const acks = useRef([]);
  useEffect(() => {
    ackCache[lang] = ackCache[lang] || Promise.all((ACKS[lang] || ACKS.en).map(tts)).then((b) => b.filter(Boolean));
    ackCache[lang].then((b) => { acks.current = b; });
  }, [lang]);

  const onHeard = useCallback(async (blob, type) => {
    setPhase("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, type.includes("mp4") ? "voice.mp4" : "voice.webm");
      fd.append("lang", lang);
      const { data } = await http.post("/voice/transcribe", fd);
      if (!active.current) return;
      if (!data.text) throw new Error("empty");
      setHeardText(data.text);
      setNote("");
      setPhase("thinking");
      waiting.current = true;
      opened.current = null;
      cancel();
      send(data.text, { voice: true });
      if (acks.current.length) {
        speech.open("ack");
        speech.pushBlob(acks.current[Math.floor(Math.random() * acks.current.length)]);
        speech.close();
      }
      startMonitorRef.current();
    } catch {
      if (!active.current) return;
      setNote(s.micError);
      setPhase("idle");
    }
  }, [lang, send, cancel, s.micError]);

  const onSilent = useCallback(() => active.current && setPhase("idle"), []);
  const { listen, stop } = useListener(session, lang, onHeard, onSilent, setLevel);

  const startListening = useCallback((preheard = false) => {
    turn.current += 1;
    stopMonitor();
    speech.stop();
    setPhase("listening");
    listen(preheard);
  }, [listen]);

  // User talks over Ceres: drop the current answer and listen straight away.
  const bargeIn = useCallback(() => {
    if (waiting.current) cancel();
    waiting.current = false;
    opened.current = null;
    startListening(true);
  }, [cancel, startListening]);

  // While Ceres thinks or speaks, watch the mic for the user starting to talk.
  const startMonitorRef = useRef(() => {});
  startMonitorRef.current = () => {
    stopMonitor();
    const { analyser } = session;
    const buf = new Uint8Array(analyser.fftSize);
    let noise = 0.01;
    let loud = 0;
    monitor.current = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      setLevel(Math.min(1, rms * 8));
      if (rms > Math.max(BARGE_RMS, noise * 2.5)) loud += 1;
      else { loud = 0; noise = noise * 0.95 + rms * 0.05; }
      if (loud >= BARGE_TICKS) bargeIn();
    }, 50);
  };

  useEffect(() => {
    active.current = true;
    startListening();
    return () => { active.current = false; stopMonitor(); stop(false); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Speak the answer sentence by sentence while it is still streaming.
  const last = messages[messages.length - 1];
  useEffect(() => {
    if (!waiting.current || !last || last.role !== "assistant") return;
    const body = spokenBody(last.text || "");
    if (!opened.current) {
      if (last.pending && !takeSentences(body, 0)[0]) return;
      if (!last.pending && (!last.text || last.error)) { waiting.current = false; setPhase("idle"); stopMonitor(); return; }
      opened.current = last.id;
      spoken.current = 0;
      said.current = 0;
      setPhase("speaking");
      const myTurn = turn.current;
      speech.open(last.id).then(() => {
        if (active.current && turn.current === myTurn) startListening();
      });
    }
    while (said.current < MAX_SPOKEN) {
      const [chunk, next] = takeSentences(body, spoken.current);
      if (!chunk) break;
      speech.push(chunk);
      spoken.current = next;
      said.current += 1;
    }
    if (!last.pending) {
      const rest = said.current < MAX_SPOKEN ? body.slice(spoken.current).trim() : "";
      if (rest) speech.push(rest);
      speech.push(closingLine(lang));
      speech.close();
      waiting.current = false;
    }
  }, [last, lang, startListening]);

  const exit = useCallback(() => {
    active.current = false;
    stopMonitor();
    if (waiting.current) cancel();
    stop(false);
    speech.stop();
    session.stream.getTracks().forEach((t) => t.stop());
    session.ctx.close?.();
    onExit();
  }, [onExit, session, stop, cancel]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && exit();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [exit]);

  const tapOrb = () => {
    if (phase === "listening") return stop(true);
    if (phase === "speaking" || phase === "thinking") {
      if (waiting.current) cancel();
      waiting.current = false;
      opened.current = null;
      return startListening();
    }
    if (phase === "idle") return startListening();
  };

  const label = { listening: s.vmListening, transcribing: s.vmTranscribing, thinking: s.vmThinking, speaking: s.vmSpeaking, idle: s.vmIdle }[phase];
  const answer = phase === "thinking" || phase === "transcribing" ? "" : last?.role === "assistant" ? plain(last.text || "") : "";

  return (
    <div className="ceres-intro absolute inset-0 z-20 flex flex-col text-white" role="dialog" aria-modal="true" aria-label={s.voiceMode} data-testid="voice-mode">
      <div className="ceres-grain-field" aria-hidden="true" />
      <header className="relative flex items-center justify-between px-5 py-4">
        <span className="font-heading text-[12px] font-semibold uppercase tracking-[0.2em] text-white/80">{s.voiceMode}</span>
        <button type="button" onClick={exit} aria-label={s.vmEnd} data-testid="voice-mode-close-button" className="p-1 text-white/80 hover:text-white"><X size={20} /></button>
      </header>

      <div className="relative flex flex-1 flex-col items-center justify-center px-6">
        <button type="button" onClick={tapOrb} aria-label={label} data-testid="voice-mode-orb" data-phase={phase}
          className="relative flex h-44 w-44 items-center justify-center rounded-full focus-visible:outline-offset-8">
          <span className="absolute inset-0 rounded-full border-2 border-hv-orange/40 transition-transform duration-100"
            style={{ transform: `scale(${1 + level * 0.35})` }} aria-hidden="true" />
          <span className="absolute inset-3 rounded-full border border-white/15 transition-transform duration-150"
            style={{ transform: `scale(${1 + level * 0.18})` }} aria-hidden="true" />
          {(phase === "thinking" || phase === "transcribing") && <span className="ceres-vm-spin absolute inset-0 rounded-full border-2 border-transparent border-t-hv-orange" aria-hidden="true" />}
          {phase === "speaking" && <span className="ceres-pulse absolute inset-0 rounded-full" style={{ animationIterationCount: "infinite", animationDelay: "0ms" }} aria-hidden="true" />}
          <span className="flex h-32 w-32 items-center justify-center rounded-full bg-hv-navy/70 backdrop-blur-sm">
            <CeresMark size={96} tone="dark" className={phase === "speaking" ? "ceres-orbit" : ""} />
          </span>
        </button>

        <p className="mt-8 font-heading text-[15px] font-semibold" aria-live="polite" data-testid="voice-mode-status">{label}</p>
        {phase === "speaking" && (
          <span className="mt-3 flex h-4 items-end gap-[3px]" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => <span key={i} className="ceres-eq w-[3px] bg-hv-orange" style={{ animationDelay: `${i * 110}ms` }} />)}
          </span>
        )}
        {note && <p className="mt-2 text-[12px] text-hv-orange" role="alert" data-testid="voice-mode-note">{note}</p>}

        <div className="mt-8 w-full max-w-md space-y-3 text-center">
          {heardText && <p className="text-[13px] text-white/60" data-testid="voice-mode-heard">“{heardText}”</p>}
          {answer && <p className="line-clamp-5 text-[14px] leading-relaxed text-white/90" data-testid="voice-mode-answer">{answer}</p>}
        </div>
      </div>

      <footer className="relative flex flex-col items-center gap-3 px-6 pb-6">
        <p className="text-center text-[11px] text-white/55">{s.vmHint}</p>
        <div className="flex gap-3">
          {phase === "idle" && (
            <button type="button" onClick={() => startListening()} className="hv-btn-dark inline-flex items-center gap-2" data-testid="voice-mode-talk-button"><Mic size={14} aria-hidden="true" />{s.vmTalk}</button>
          )}
          <button type="button" onClick={exit} className="hv-btn-dark" data-testid="voice-mode-end-button">{s.vmEnd}</button>
        </div>
      </footer>
    </div>
  );
};
